import { API_BASE_URL } from '@synergy/config';
import { FormattedMessage } from 'react-intl';
import { Locale } from '@synergy/models';
import { connect } from 'react-redux';
import { getLocalizedString, padZeroLeft } from '@synergy/utils';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { PoolFormAutocompleteList, PoolFormAutocompleteListProps } from '../../abstract/form-autocomplete-list';
import { ProductSearchApi } from '../../../../features/products/product/product-detail/variants/api/product-search-api';
import { ProductVariant } from '../../../../features/products/product/product-detail/variants/models/product-detail-variant';
import { bestLocaleSelector, localesSelector } from '../../../selectors/locale-selectors';

export interface PartialProductVariant extends BaseAutocompleteSuggestionType {
    name: string;
    number: string;
    attributeLabels: string;
}

interface PoolProductVariantsAutocompleteListOwnProps
    extends Omit<PoolFormAutocompleteListProps<ProductVariant, PartialProductVariant>, 'items'> {
    productId?: number;
}

interface PoolProductVariantsAutocompleteListReduxProps {
    locales: Locale[];
    locale: Locale;
}

type PoolProductVariantsAutocompleteListProps = PoolProductVariantsAutocompleteListOwnProps &
    PoolProductVariantsAutocompleteListReduxProps;

class PoolProductVariantsAutocompleteListBase extends PoolFormAutocompleteList<
    ProductVariant,
    PartialProductVariant,
    PoolProductVariantsAutocompleteListProps
> {
    public resetSearch = () => this.resetSearchParams();
    public label = this.props.label || <FormattedMessage id="app.label.product" />;
    public hideId = true;
    private api = new ProductSearchApi(API_BASE_URL);
    private apiAbortController?: AbortController;
    private searchParams = new URLSearchParams({ limit: '40', locale: this.props.locale });

    private resetSearchParams() {
        this.searchParams = new URLSearchParams({ limit: '40', locale: this.props.locale });
    }

    public componentDidMount() {
        this.setState({ items: [] });
        this.resetSearchParams();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: PoolProductVariantsAutocompleteListProps): void {
        if (this.props.locale !== nextProps.locale) {
            this.setState({ items: [] });
            this.searchParams.set('locale', nextProps.locale);
        }
    }

    public getSuggestionValue = (suggestion: PartialProductVariant) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        if (!this.props.productId) {
            return;
        }
        this.searchParams.set('search', search);
        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.searchVariants(
            this.props.productId,
            this.searchParams,
            this.apiAbortController.signal
        );
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success === true ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (items: ProductVariant[]): PartialProductVariant[] => {
        return items.map(productVariant => {
            return {
                id: padZeroLeft(productVariant.id, 5),
                number: productVariant.number || '',
                name: getLocalizedString(this.props.locales, productVariant.name),
                attributeLabels: productVariant.attributeLabels[this.props.locale]?.join(' | ') ?? <span>&ndash;</span>,
            };
        });
    };
}

const mapStateToProps = (state: AppState) => ({
    locales: localesSelector(state),
    locale: bestLocaleSelector(state),
});

export const PoolProductVariantsAutocompleteList = connect(
    mapStateToProps,
    {}
)(PoolProductVariantsAutocompleteListBase);
